import { createSelector } from '@reduxjs/toolkit';
import { useSelector } from 'react-redux';


import type { AssignedTablesType } from '../../../api/fetchers/assignedTables/types';
import {
  PromotionType,
  type PrizeDropWinSocketResponse,
  type Promotion,
  type RoundHistoryByWSType,
  type RoundHistoryType,
  type WinNotificationType,
} from '../../../api/fetchers/promotionsV2/types/promotions';
import { BonusCardPromotionPointsJSON } from '../../../api/fetchers/promotionsV2/types/promotionsApi';

import type {
  PromoFeatures,
  PromoPoint,
  PromosApiAuthData,
  PromosCoreState,
  PromosGameData,
  PromosJwtAuthData,
  PromosPopup,
  PromosUserData,
  TopTournamentPoints,
} from './types';
import { isPromoOpted } from './utils';

const selectOptedPromotions = createSelector(
  [(state: PromosCoreState): Promotion[] => state.promotionsV2.promotions],
  (promotions): Promotion[] => promotions.filter(isPromoOpted)
);

const selectPromoById = createSelector(
  [
    (state: PromosCoreState): Promotion[] => state.promotionsV2.promotions,
    (state: PromosCoreState): number | null => state.promotionsV2.selectedPromoId,
  ],
  (promotions, id): Promotion | undefined => promotions.find((promo) => promo.id === id)
);

const selectPromosUserData = createSelector(
  [
    (state: PromosCoreState): string | undefined => state.promotionsV2.userId,
    (state: PromosCoreState): string | undefined => state.promotionsV2.lang,
    (state: PromosCoreState): string | undefined => state.promotionsV2.screenName,
    (state: PromosCoreState): string | undefined => state.promotionsV2.casinoRegion,
  ],
  (userId, lang, screenName, casinoRegion): PromosUserData | null =>
    isSet(userId) && isSet(lang) && isSet(screenName) && isSet(casinoRegion)
      ? { userId, lang, screenName, casinoRegion }
      : null
);

const selectPromosGameData = createSelector(
  [
    (state: PromosCoreState): string | undefined => state.promotionsV2.gameId,
    (state: PromosCoreState): string | undefined => state.promotionsV2.currency,
    (state: PromosCoreState): boolean | undefined => state.promotionsV2.isBetOpenStatus,
    (state: PromosCoreState): number | undefined => state.promotionsV2.totalBet,
  ],
  (gameId, currency, isBetOpenStatus, totalBet): PromosGameData | null =>
    isSet(gameId) && isSet(currency) && isSet(isBetOpenStatus) && isSet(totalBet)
      ? { gameId, currency, isBetOpenStatus, totalBet }
      : null
);

const selectPromosApiAuthData = createSelector(
  [
    (state: PromosCoreState): string | undefined => state.promotionsV2.envPromo,
    (state: PromosCoreState): string | undefined => state.promotionsV2.jwtToken,
  ],
  (envPromo, jwtToken): PromosApiAuthData | null => (isSet(envPromo) && isSet(jwtToken) ? { envPromo, jwtToken } : null)
);

const selectPromosJwtAuthData = createSelector(
  [
    (state: PromosCoreState): string | undefined => state.promotionsV2.envGs,
    (state: PromosCoreState): string | undefined => state.promotionsV2.jsessionId,
  ],
  (envGs, jsessionId): PromosJwtAuthData | null => (isSet(envGs) && isSet(jsessionId) ? { envGs, jsessionId } : null)
);

const selectCancelledBonusCardPromotionIds = createSelector(
  [(state: PromosCoreState): PromoPoint[] => state.promotionsV2.points],
  (points): number[] =>
    points
      .filter(
        (item) =>
          item.promoType === PromotionType.BonusCard && (item as BonusCardPromotionPointsJSON).isBonusCardCancelled
      )
      .map((item) => item.id)
);

const selectCardLimitExceededBonusCardPromotionIds = createSelector(
  [(state: PromosCoreState): PromoPoint[] => state.promotionsV2.points],
  (points): number[] =>
    points
      .filter(
        (item) =>
          item.promoType === PromotionType.BonusCard && (item as BonusCardPromotionPointsJSON).isCardsLimitExceeded
      )
      .map((item) => item.id)
);

export const usePromotionsV2Slice = {
  getIsPromotionsPaused: (): boolean => {
    return useSelector<PromosCoreState, boolean>(({ promotionsV2 }: PromosCoreState) => {
      return promotionsV2.isPromotionsPaused;
    });
  },
  getPromotions: (): Array<Promotion> => {
    return useSelector<PromosCoreState, Array<Promotion>>(({ promotionsV2 }: PromosCoreState) => {
      return promotionsV2.promotions;
    });
  },
  getPoints: (): Array<PromoPoint> => {
    return useSelector<PromosCoreState, Array<PromoPoint>>(({ promotionsV2 }: PromosCoreState) => {
      return promotionsV2.points;
    });
  },
  getAssignedTables: (): AssignedTablesType | null => {
    return useSelector<PromosCoreState, AssignedTablesType | null>(({ promotionsV2 }: PromosCoreState) => {
      return promotionsV2.assignedTables;
    });
  },
  getWinNotifications: (): WinNotificationType[] => {
    return useSelector<PromosCoreState, WinNotificationType[]>(({ promotionsV2 }: PromosCoreState) => {
      return promotionsV2.winNotifications;
    });
  },
  getOptedPromotions: (): Array<Promotion> => {
    return useSelector<PromosCoreState, Array<Promotion>>(selectOptedPromotions);
  },
  getSelectedPromo: (): Promotion | null | undefined => {
    return useSelector<PromosCoreState, Promotion | null | undefined>(selectPromoById);
  },
  getPromosPopup: (): PromosPopup | null => {
    return useSelector<PromosCoreState, PromosPopup | null>(({ promotionsV2 }: PromosCoreState) => {
      return promotionsV2.promosPopup;
    });
  },
  getShowPromoEar: (): boolean => {
    return useSelector<PromosCoreState, boolean>(({ promotionsV2 }: PromosCoreState) => {
      return promotionsV2.showPromoEar;
    });
  },
  getShowScorePopup: (): boolean => {
    return useSelector<PromosCoreState, boolean>(({ promotionsV2 }: PromosCoreState) => {
      return promotionsV2.showScorePopup;
    });
  },
  getShowDetailsModal: (): boolean => {
    return useSelector<PromosCoreState, boolean>(({ promotionsV2 }: PromosCoreState) => {
      return promotionsV2.showDetailsModal;
    });
  },
  getOptPromosLoadingStatus: (): boolean => {
    return useSelector<PromosCoreState, boolean>(({ promotionsV2 }: PromosCoreState) => {
      return promotionsV2.isOptPromosLoading;
    });
  },
  getTopTournament: (): TopTournamentPoints | undefined => {
    return useSelector<PromosCoreState, TopTournamentPoints | undefined>(({ promotionsV2 }: PromosCoreState) => {
      return promotionsV2.topTournament;
    });
  },
  getIsNewPromoAvailable: (): boolean => {
    return useSelector<PromosCoreState, boolean>(({ promotionsV2 }: PromosCoreState) => {
      return promotionsV2.isNewPromoAvailable;
    });
  },
  getCancelledBonusCardPromotionIds: (): number[] => {
    return useSelector(selectCancelledBonusCardPromotionIds);
  },
  getCardLimitExceededBonusCardPromotionIds: (): number[] => {
    return useSelector(selectCardLimitExceededBonusCardPromotionIds);
  },
  getPromosUserData: (): PromosUserData | null => {
    return useSelector<PromosCoreState, PromosUserData | null>(selectPromosUserData);
  },
  getPromosGameData: (): PromosGameData | null => {
    return useSelector<PromosCoreState, PromosGameData | null>(selectPromosGameData);
  },
  getPromosApiAuthData: (): PromosApiAuthData | null => {
    return useSelector<PromosCoreState, PromosApiAuthData | null>(selectPromosApiAuthData);
  },
  getPromosJwtAuthData: (): PromosJwtAuthData | null => {
    return useSelector<PromosCoreState, PromosJwtAuthData | null>(selectPromosJwtAuthData);
  },
  getPrizeDropWinMessage: (): PrizeDropWinSocketResponse | null => {
    return useSelector<PromosCoreState, PrizeDropWinSocketResponse | null>(({ promotionsV2 }: PromosCoreState) => {
      return promotionsV2.prizeDropWinMessage ?? null;
    });
  },
  getRoundHistory: (): RoundHistoryType[] => {
    return useSelector<PromosCoreState, RoundHistoryType[]>(({ promotionsV2 }: PromosCoreState) => {
      const { roundHistory } = promotionsV2;
      return roundHistory;
    });
  },
  getRoundHistoryByWs: (): RoundHistoryByWSType[] => {
    return useSelector<PromosCoreState, RoundHistoryByWSType[]>(({ promotionsV2 }: PromosCoreState) => {
      const { roundHistoryByWs } = promotionsV2;
      return roundHistoryByWs;
    });
  },
  getRoundHistoryLoadingStatus: (): boolean => {
    return useSelector<PromosCoreState, boolean>(({ promotionsV2 }: PromosCoreState) => {
      const { isRoundHistoryLoading } = promotionsV2;
      return isRoundHistoryLoading;
    });
  },
  getIsResponsive: (): boolean | undefined => {
    return useSelector<PromosCoreState, boolean | undefined>(
      ({ promotionsV2 }: PromosCoreState) => promotionsV2.isResponsive
    );
  },
  getPromoFeatureConfig: (): PromoFeatures | undefined => {
    return useSelector<PromosCoreState, PromoFeatures | undefined>(
      ({ promotionsV2 }: PromosCoreState) => promotionsV2.featureConfig
    );
  },
  getSelectedPromoId: (): number | null => {
    return useSelector<PromosCoreState, number | null>(
      ({ promotionsV2 }: PromosCoreState) => promotionsV2.selectedPromoId
    );
  },
  getIsPromotionsInitialised: (): boolean => {
    return useSelector<PromosCoreState, boolean>(
      ({ promotionsV2 }: PromosCoreState) => promotionsV2.isPromotionsInitialised
    );
  },
};

function isSet<T>(val?: T): val is T {
  return val !== undefined;
}
